/**
 * FeedbackModal — Submit bug reports and feature requests
 *
 * Public submit form, no auth required. Auto-captures browser diagnostics.
 */

import { useState, useCallback, useRef } from 'react';
import { Modal } from '@/components/atoms/Modal';
import { submitFeedback } from '@/lib/infra/feedbackApi';
import type { FeedbackType } from '@galactus/shared';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const typeOptions: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [text, setText] = useState('');
  const [type, setType] = useState<FeedbackType>('bug');
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []).slice(0, 5);
    setFiles(selected);
    // Generate thumbnail previews
    const urls = selected.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
  }, []);

  const reset = useCallback(() => {
    setText('');
    setType('bug');
    setFiles([]);
    setPreviews((prev) => {
      prev.forEach((u) => URL.revokeObjectURL(u));
      return [];
    });
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!text.trim()) return;

      setSubmitting(true);
      setError(null);

      try {
        // Convert files to base64
        const attachments: { name: string; data: string }[] = [];
        for (const file of files) {
          const data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
          });
          attachments.push({ name: file.name, data });
        }

        await submitFeedback({ text: text.trim(), type, attachments });
        setSuccess(true);
        setTimeout(() => handleClose(), 2000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to submit');
      } finally {
        setSubmitting(false);
      }
    },
    [text, type, files, handleClose]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Send Feedback"
      size="md"
    >
      {success ? (
        <div className="py-8 text-center">
          <p className="text-foreground text-lg font-medium">Thanks! We got it.</p>
          <p className="text-muted-foreground mt-1 text-sm">This will close automatically.</p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* Type selector */}
          <div className="form-group">
            <label htmlFor="feedback-type">Type</label>
            <select
              id="feedback-type"
              value={type}
              onChange={(e) => setType(e.target.value as FeedbackType)}
              className="bg-input border-border text-foreground w-full rounded border px-3 py-2 text-sm"
            >
              {typeOptions.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="feedback-text">Description</label>
            <textarea
              id="feedback-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What happened? What did you expect?"
              rows={4}
              maxLength={5000}
              required
              className="bg-input border-border text-foreground w-full rounded border px-3 py-2 text-sm"
            />
            <p className="text-muted-foreground mt-1 text-xs">{text.length}/5000</p>
          </div>

          {/* File upload */}
          <div className="form-group">
            <label htmlFor="feedback-files">Screenshots (optional)</label>
            <input
              ref={fileInputRef}
              id="feedback-files"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="text-muted-foreground text-sm"
            />
            {previews.length > 0 && (
              <div className="mt-2 flex gap-2">
                {previews.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Preview ${i + 1}`}
                    className="border-border h-16 w-16 rounded border object-cover"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-destructive/10 border-destructive/20 rounded border p-2">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !text.trim()}
            className="btn-primary w-full"
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      )}
    </Modal>
  );
}
